<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fee-create-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="fee-create-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalDescription }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.modal.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__body">
        <aside class="app-modal__menu" :aria-label="text.modal.sectionsAriaLabel">
          <button
            v-for="section in resolvedSections"
            :key="section.key"
            class="app-modal__menu-item"
            :class="{ active: activeSectionKey === section.key }"
            type="button"
            @click="activeSectionKey = section.key"
          >
            <span class="app-modal__menu-icon">
              <i class="fa" :class="section.icon" aria-hidden="true"></i>
            </span>
            <span class="app-modal__menu-copy">
              <strong>{{ section.title }}</strong>
              <small>{{ section.description }}</small>
            </span>
          </button>
        </aside>

        <div class="app-modal__content">
          <form class="app-modal__form" @submit.prevent="submitForm">
            <FeeModalSectionGeneral
              :active="activeSectionKey === 'general'"
              :form="form"
              :options="options"
              :is-edit-mode="isEditMode"
              :is-fee-paid="isFeePaid"
              :status-label="feeStatusLabel"
              :is-hydrating="isHydratingInitialData"
              :tax-preview="taxPreview"
              :tax-preview-loading="taxPreviewLoading"
              :first-installment-options="firstInstallmentOptions"
              :warnings="warnings"
              :v$="v$"
            />

            <FeeModalSectionOperational
              :active="activeSectionKey === 'operational'"
              :form="form"
              :options="options"
              :hide-sale-customer-fields="Number(lockedSaleId || 0) > 0"
            />

            <FeeModalSectionDetails
              :active="activeSectionKey === 'details'"
              :form="form"
              :options="options"
            />

            <FeeModalSectionFiles
              :active="activeSectionKey === 'files'"
              :form="form"
            />

            <footer class="app-modal__footer">
              <div class="app-modal__footer-right">
                <button type="button" class="btn btn-light" @click="emitClose">{{ text.modal.cancel }}</button>
                <button type="submit" class="btn btn-primary">
                  {{ submitting ? text.modal.submitting : submitButtonLabel }}
                </button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import taxesService from '@/services/taxes';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  FEE_MODAL_SECTIONS,
  buildFeePayload,
  buildFeeTaxPreviewPayload,
  buildFirstInstallmentOptions,
  createInitialFeeForm,
  createInitialFeeModalOptions,
  createInitialFeeTaxPreview,
} from '@/composables/fees/useFeeModalForm';
import { fetchFeeModalCatalogs } from '@/composables/fees/useFeeModalCatalogs';
import {
  applyExpenseFeeLockedSaleContext,
  resolveExpenseFeePaidState,
  resolveExpenseFeeStatusLabel,
  applyExpenseFeeTaxSelectionFromDetail,
  enforceExpenseFeeAmountConstraints,
  focusExpenseFeeFirstInvalidField,
  normalizeExpenseFeeFirstInstallmentValue,
  normalizeExpenseFeeFrequencyValue,
  resolveExpenseFeeFirstInvalidField,
} from '@/composables/expensesFees/shared/useExpenseFeeModalHydrationShared';
import FeeModalSectionGeneral from '@/components/fees/modal/FeeModalSections/FeeModalSectionGeneral.vue';
import FeeModalSectionOperational from '@/components/fees/modal/FeeModalSections/FeeModalSectionOperational.vue';
import FeeModalSectionFiles from '@/components/fees/modal/FeeModalSections/FeeModalSectionFiles.vue';
import FeeModalSectionDetails from '@/components/fees/modal/FeeModalSections/FeeModalSectionDetails.vue';
import { extractPayload } from '@/utils/dataNormalization';
import { parsePresentationDate } from '@/utils/datePresentation';
import {
  findOptionByRawValue,
  toPositiveOptionId,
} from '@/utils/optionValues';
import '@vuepic/vue-datepicker/dist/main.css';

const { catalog: text } = useDomainText('fees');

export default {
  name: 'FeeModal',
  components: {
    FeeModalSectionGeneral,
    FeeModalSectionOperational,
    FeeModalSectionFiles,
    FeeModalSectionDetails,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initialData: {
      type: Object,
      default: null,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    lockedSaleId: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      text,
      sections: FEE_MODAL_SECTIONS,
      activeSectionKey: 'general',
      optionsLoading: false,
      taxPreviewLoading: false,
      taxPreviewRequestToken: 0,
      taxPreviewDebounceTimer: null,
      taxPreviewDebounceMs: 350,
      isHydratingInitialData: false,
      warnings: {
        discount: '',
        deductions: '',
      },
      isFeePaid: false,
      feeStatusLabel: '',
      warningLocks: {
        discount: false,
        deductions: false,
      },
      form: createInitialFeeForm(),
      options: createInitialFeeModalOptions(),
      firstInstallmentOptions: buildFirstInstallmentOptions(),
      taxPreview: createInitialFeeTaxPreview(),
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || '').trim().toLowerCase() === 'edit';
    },
    modalTitle() {
      return this.isEditMode ? this.text.modal.titleEdit : this.text.modal.titleCreate;
    },
    modalDescription() {
      return this.isEditMode
        ? this.text.modal.subtitleEdit
        : this.text.modal.subtitleCreate;
    },
    submitButtonLabel() {
      return this.isEditMode ? this.text.modal.submitEdit : this.text.modal.submitCreate;
    },
    resolvedSections() {
      const sectionTextByKey = {
        general: this.text.modal.sections.general,
        operational: this.text.modal.sections.operational,
        details: this.text.modal.sections.details,
        files: this.text.modal.sections.files,
      };

      return this.sections.map((section) => {
        const copy = sectionTextByKey[section.key] || {};

        return {
          ...section,
          title: copy.title || section.key,
          description: copy.description || '',
        };
      });
    },
    hasCommissionAgent() {
      const commissionType = String(this.form?.commissionType || 'collaborator').trim().toLowerCase();
      if (commissionType === 'collaborator') {
        return toPositiveOptionId(this.form?.collaborator) > 0;
      }

      return toPositiveOptionId(this.form?.supplier) > 0;
    },
    hasLockedSaleContext() {
      return !this.isEditMode && Number(this.lockedSaleId || 0) > 0;
    },
    hasConcept() {
      return String(this.form.concept || '').trim() !== '';
    },
    hasValidAmount() {
      return Number(this.form.amount || 0) > 0;
    },
    requiresRecurringConfig() {
      return String(this.form.frequency || '').trim().toLowerCase() !== 'unique';
    },
    hasRecurringConfig() {
      if (!this.requiresRecurringConfig) {
        return true;
      }

      const frequency = String(this.form?.frequency || '').trim().toLowerCase();
      const hasCount = Number(this.form.expensesCount || 0) > 0;
      const hasFirstInstallment = String(this.form?.firstInstallmentOption?.value || '').trim() !== '';
      const hasPeriodicity = String(this.form?.periodicityOption?.value || '').trim() !== '';

      if (frequency === 'monthly') {
        return hasCount && hasFirstInstallment;
      }

      if (frequency === 'multiple') {
        return hasCount && hasPeriodicity;
      }

      return hasCount && hasFirstInstallment && hasPeriodicity;
    },
  },
  validations() {
    return {
      form: {
        supplier: {
          requiredForSupplierCommission: helpers.withMessage(
            this.text.modal.validation.supplierRequired,
            (value) => {
              const commissionType = String(this.form?.commissionType || 'collaborator').trim().toLowerCase();
              if (commissionType !== 'supplier') {
                return true;
              }

              return toPositiveOptionId(value) > 0;
            }
          ),
        },
        collaborator: {
          requiredForCollaboratorCommission: helpers.withMessage(
            this.text.modal.validation.collaboratorRequired,
            (value) => {
              const commissionType = String(this.form?.commissionType || 'collaborator').trim().toLowerCase();
              if (commissionType !== 'collaborator') {
                return true;
              }

              return toPositiveOptionId(value) > 0;
            }
          ),
        },
        concept: {
          required: helpers.withMessage(
            this.text.modal.validation.conceptRequired,
            (value) => String(value || '').trim() !== ''
          ),
        },
        amount: {
          validAmount: helpers.withMessage(
            this.text.modal.validation.amountInvalid,
            (value) => Number(value || 0) > 0
          ),
        },
        expensesCount: {
          validRecurringCount: helpers.withMessage(
            this.text.modal.validation.recurringCountInvalid,
            () => Number(this.form?.expensesCount || 0) > 0
          ),
        },
        firstInstallmentOption: {
          requiredForMonthly: helpers.withMessage(
            this.text.modal.validation.firstInstallmentRequired,
            (value) => {
              const frequency = String(this.form?.frequency || '').trim().toLowerCase();
              if (frequency !== 'monthly') {
                return true;
              }

              return String(value?.value || '').trim() !== '';
            }
          ),
        },
        periodicityOption: {
          requiredForMultiple: helpers.withMessage(
            this.text.modal.validation.periodicityRequired,
            (value) => {
              const frequency = String(this.form?.frequency || '').trim().toLowerCase();
              if (frequency !== 'multiple') {
                return true;
              }

              return String(value?.value || '').trim() !== '';
            }
          ),
        },
      },
    };
  },
  setup() {
    const v$ = useVuelidate();

    return { v$ };
  },
  watch: {
    show(visible) {
      if (visible) {
        this.handleOpen();
        return;
      }

      this.resetState();
      this.v$.$reset();
    },
    'form.amount'() {
      this.enforceAmountConstraints('amount');
      this.scheduleTaxPreview();
    },
    'form.discount'() {
      this.enforceAmountConstraints('discount');
      this.scheduleTaxPreview();
    },
    'form.deductions'() {
      this.enforceAmountConstraints('deductions');
      this.scheduleTaxPreview();
    },
    'form.taxType'() {
      this.scheduleTaxPreview();
    },
    'form.taxes': {
      deep: true,
      handler() {
        this.scheduleTaxPreview();
      },
    },
    'form.taxGroup'() {
      this.scheduleTaxPreview();
    },
    'form.withoutTaxes'() {
      this.scheduleTaxPreview();
    },
    'form.localTaxes': {
      deep: true,
      handler() {
        this.scheduleTaxPreview();
      },
    },
  },
  beforeUnmount() {
    this.clearTaxPreviewDebounce();
  },
  methods: {
    resetState() {
      this.activeSectionKey = this.sections[0].key;
      this.form = createInitialFeeForm();
      this.options = createInitialFeeModalOptions();
      this.firstInstallmentOptions = buildFirstInstallmentOptions();
      this.taxPreview = createInitialFeeTaxPreview();
      this.taxPreviewRequestToken = 0;
      this.taxPreviewLoading = false;
      this.optionsLoading = false;
      this.warnings = {
        discount: '',
        deductions: '',
      };
      this.isFeePaid = false;
      this.feeStatusLabel = '';
      this.warningLocks = {
        discount: false,
        deductions: false,
      };
      this.isHydratingInitialData = false;
      this.clearTaxPreviewDebounce();
    },
    async handleOpen() {
      this.resetState();
      await this.loadOptions();

      if (this.initialData && typeof this.initialData === 'object') {
        this.isHydratingInitialData = true;
        const hydratedData = this.applyInitialDataToForm(this.initialData);
        this.applyCreateDefaultTaxFromHydratedData(hydratedData);
        await this.$nextTick();
        this.feeStatusLabel = resolveExpenseFeeStatusLabel({
          data: hydratedData,
          statusLabels: this.text?.modal?.statusLabels,
        });
        this.isFeePaid = resolveExpenseFeePaidState({
          data: {
            ...hydratedData,
            status: this.feeStatusLabel,
          },
        });
        this.isHydratingInitialData = false;
      }

      this.v$.$reset();

      this.applyLockedSaleContext();

      this.scheduleTaxPreview();
    },
    hasExplicitTaxSelectionInData(data = {}) {
      return (
        Object.prototype.hasOwnProperty.call(data, 'without_taxes')
        || Object.prototype.hasOwnProperty.call(data, 'withoutTaxes')
        || Object.prototype.hasOwnProperty.call(data, 'no_taxes')
        || Object.prototype.hasOwnProperty.call(data, 'tax_type')
        || Object.prototype.hasOwnProperty.call(data, 'taxType')
        || Object.prototype.hasOwnProperty.call(data, 'tax_id')
        || Object.prototype.hasOwnProperty.call(data, 'tax_group_id')
        || Object.prototype.hasOwnProperty.call(data, 'tax_ids')
        || Object.prototype.hasOwnProperty.call(data, 'tax')
        || Object.prototype.hasOwnProperty.call(data, 'taxes')
        || Object.prototype.hasOwnProperty.call(data, 'tax_group')
      );
    },
    resolveDefaultTaxOption() {
      return (Array.isArray(this.options.taxes) ? this.options.taxes : [])
        .find((option) => Number(option?.meta?.default || 0) === 1) || null;
    },
    applyCreateDefaultTaxFromHydratedData(data = {}) {
      if (this.isEditMode || this.hasExplicitTaxSelectionInData(data)) {
        return;
      }

      const hasIndividualTax = Array.isArray(this.form.taxes)
        && this.form.taxes.some((option) => toPositiveOptionId(option) > 0);
      const hasTaxGroup = toPositiveOptionId(this.form.taxGroup) > 0;
      if (hasIndividualTax || hasTaxGroup) {
        return;
      }

      const defaultTax = this.resolveDefaultTaxOption();
      if (!defaultTax) {
        return;
      }

      this.form.withoutTaxes = false;
      this.form.taxType = 'individual';
      this.form.taxGroup = null;
      this.form.taxes = [defaultTax];
    },
    emitClose() {
      this.$emit('close');
    },
    clearTaxPreviewDebounce() {
      if (!this.taxPreviewDebounceTimer) {
        return;
      }

      clearTimeout(this.taxPreviewDebounceTimer);
      this.taxPreviewDebounceTimer = null;
    },
    scheduleTaxPreview() {
      if (!this.show) {
        return;
      }

      this.clearTaxPreviewDebounce();
      this.taxPreviewDebounceTimer = setTimeout(() => {
        this.taxPreviewDebounceTimer = null;
        this.fetchTaxPreview();
      }, this.taxPreviewDebounceMs);
    },
    async loadOptions() {
      this.optionsLoading = true;

      try {
        const catalogs = await fetchFeeModalCatalogs();
        this.options = {
          ...this.options,
          ...catalogs,
        };

        const firstCurrency = this.options.currencies[0]?.value;
        if (firstCurrency && String(this.form.currencyCode || '').trim() === '') {
          this.form.currencyCode = String(firstCurrency).trim().toUpperCase();
        }

        const defaultTax = (Array.isArray(this.options.taxes) ? this.options.taxes : [])
          .find((option) => Number(option?.meta?.default || 0) === 1);
        if (defaultTax && String(this.form?.taxType || '').trim().toLowerCase() === 'individual') {
          this.form.taxes = [defaultTax];
        }
      } finally {
        this.optionsLoading = false;
      }
    },
    applyTaxSelectionFromDetail(data = {}) {
      this.options = applyExpenseFeeTaxSelectionFromDetail({
        data,
        form: this.form,
        options: this.options,
      });
    },
    applyInitialDataToForm(initialData) {
      const source = initialData && typeof initialData === 'object' ? initialData : {};
      const data = source?.data && typeof source.data === 'object' ? source.data : source;

      const commissionType = String(data.commission_type || data.type_commission || '').trim().toLowerCase();
      const providerId = Number(data.provider_id || data.supplier_id || data.supplier?.id || data.provider?.id || 0);
      const collaboratorId = Number(data.collaborator_id || data.collaborator?.id || 0);

      if (commissionType === 'supplier' || commissionType === 'collaborator') {
        this.form.commissionType = commissionType;
      } else if (providerId > 0 && collaboratorId <= 0) {
        this.form.commissionType = 'supplier';
      } else if (collaboratorId > 0 && providerId <= 0) {
        this.form.commissionType = 'collaborator';
      }

      const voucherType = String(data.voucher_type || data.voucherType || '').trim().toLowerCase();
      if (voucherType !== '') {
        this.form.voucherType = voucherType;
      }

      const expenseStatus = String(data.expense_status || data.status || '').trim().toLowerCase();
      if (expenseStatus !== '') {
        this.form.expenseStatus = expenseStatus;
      }

      const currencyCode = String(data.currency || data.currencyCode || '').trim().toUpperCase();
      if (currencyCode !== '') {
        this.form.currencyCode = currencyCode;
      }

      this.form.exchangeRate = Number(data.exchange_rate || data.type_change || this.form.exchangeRate || 1) || 1;
      this.form.amount = Number(data.amount || 0) || 0;
      this.form.discount = Number(data.discount || 0) || 0;
      this.form.deductions = Number(data.deductions || 0) || 0;

      this.form.supplier = findOptionByRawValue(
        this.options.suppliers,
        [data.supplier, data.provider, data.supplier_id, data.provider_id]
      );

      this.form.collaborator = findOptionByRawValue(
        this.options.collaborators,
        [data.collaborator, data.collaborator_id]
      );

      this.form.supplierContributor = findOptionByRawValue(
        this.options.supplierContributors,
        [data.provider_contributor_id, data.supplier_contributor_id, data.supplier_contributor]
      );

      const expenseClassId = Number(data.class_id || data.class?.id || 0);
      this.form.expenseClass = expenseClassId > 0
        ? {
          value: expenseClassId,
          label: String(data.class?.name || '').trim() || `Clase #${expenseClassId}`,
        }
        : null;

      const categoryId = Number(data.category_id || data.category?.id || 0);
      this.form.expenseCategory = categoryId > 0
        ? {
          value: categoryId,
          label: String(data.category?.name || '').trim() || `Categoría #${categoryId}`,
        }
        : null;

      this.form.customer = findOptionByRawValue(
        this.options.customers,
        [data.company, data.customer, data.company_id, data.customer_id]
      );

      this.form.sale = findOptionByRawValue(
        this.options.sales,
        [data.sale, data.sale_id],
        { metaKeys: ['sale_id'] }
      );

      this.form.area = findOptionByRawValue(
        this.options.areas,
        [data.area, data.area_id, data.supplier?.area_id]
      );

      this.applyTaxSelectionFromDetail(data);

      this.form.localTaxes = Array.isArray(data.local_taxes)
        ? data.local_taxes.map((item) => ({
          type: String(item?.type || 'trasladado').trim().toLowerCase(),
          name: String(item?.name || '').trim(),
          percentage: Number(item?.percentage || 0) || 0,
          amount: Number(item?.amount || 0) || 0,
        }))
        : [];

      const frequency = normalizeExpenseFeeFrequencyValue(data.frequency);
      if (frequency) {
        this.form.frequency = frequency;
      }

      this.form.expensesCount = Math.max(1, Number(data.expenses_count || data.expensesCount || 1) || 1);

      const firstInstallment = normalizeExpenseFeeFirstInstallmentValue(data.first_installment || data.first_month);
      if (firstInstallment !== '') {
        this.form.firstInstallmentOption = findOptionByRawValue(this.firstInstallmentOptions, firstInstallment);
      }

      const periodicityCode = String(data.periodicity_code || '').trim().toLowerCase();
      if (periodicityCode !== '') {
        this.form.periodicityOption = findOptionByRawValue(this.options.periodicities, periodicityCode);
      }

      this.form.folio = String(data.folio || '').trim();
      this.form.concept = String(data.concept || '').trim();
      this.form.comments = String(data.comments || '').trim();

      const expenseDate = parsePresentationDate(data.date);
      if (expenseDate) {
        this.form.expenseDate = expenseDate;
      }

      const paymentDate = parsePresentationDate(data.payment_date);
      if (paymentDate) {
        this.form.paymentDate = paymentDate;
      }

      const cfdiDate = parsePresentationDate(data.cfdi_date || data.invoice_date);
      if (cfdiDate) {
        this.form.cfdiDate = cfdiDate;
      }

      const rawTags = Array.isArray(data.tags)
        ? data.tags
        : (Array.isArray(data.tag_ids) ? data.tag_ids : []);
      this.form.tags = rawTags
        .map((tag) => findOptionByRawValue(this.options.tags, tag))
        .filter(Boolean);

      const shouldReapplyTaxSelection = this.isEditMode || this.hasExplicitTaxSelectionInData(data);
      if (shouldReapplyTaxSelection) {
        this.$nextTick(() => {
          this.applyTaxSelectionFromDetail(data);
        });
      }

      return data;
    },
    applyLockedSaleContext() {
      applyExpenseFeeLockedSaleContext({
        form: this.form,
        options: this.options,
        lockedSaleId: this.lockedSaleId,
        fallbackSaleLabel: this.text?.modal?.fallbackSaleLabel,
      });
    },
    enforceAmountConstraints(sourceField = '') {
      enforceExpenseFeeAmountConstraints({
        form: this.form,
        warnings: this.warnings,
        warningLocks: this.warningLocks,
        sourceField,
        warningMessages: this.text?.modal?.warnings,
      });
    },
    async fetchTaxPreview() {
      const requestToken = this.taxPreviewRequestToken + 1;
      this.taxPreviewRequestToken = requestToken;
      this.taxPreviewLoading = true;

      try {
        const response = await taxesService.preview(
          buildFeeTaxPreviewPayload(this.form),
          { skipLoading: true }
        );
        if (requestToken !== this.taxPreviewRequestToken) {
          return;
        }

        const payload = extractPayload(response);
        this.taxPreview = {
          ...createInitialFeeTaxPreview(),
          ...(payload && typeof payload === 'object' ? payload : {}),
        };
      } catch (error) {
        if (requestToken === this.taxPreviewRequestToken) {
          this.taxPreview = createInitialFeeTaxPreview();
        }
      } finally {
        if (requestToken === this.taxPreviewRequestToken) {
          this.taxPreviewLoading = false;
        }
      }
    },
    submitForm() {
      if (this.submitting) {
        return;
      }

      this.v$.$touch();

      if (this.v$.$invalid) {
        this.activeSectionKey = 'general';
        this.focusFirstInvalidField();

        return;
      }

      const payload = buildFeePayload(this.form, this.taxPreview);

      if (this.hasLockedSaleContext) {
        payload.sale_id = Number(this.lockedSaleId || 0);
      }

      this.$emit('submit', payload);
    },
    resolveFirstInvalidGeneralField() {
      return resolveExpenseFeeFirstInvalidField({
        validation: this.v$?.form,
        orderedFields: [
          'supplier',
          'collaborator',
          'concept',
          'amount',
          'expensesCount',
          'firstInstallmentOption',
          'periodicityOption',
        ],
      });
    },
    focusFirstInvalidField() {
      const invalidField = this.resolveFirstInvalidGeneralField();
      if (invalidField === '') {
        return;
      }
      focusExpenseFeeFirstInvalidField({
        vm: this,
        invalidField,
        selectorMap: {
          supplier: [
            '[data-focus-field="supplier"] .multiselect__input',
            '[data-focus-field="supplier"] .multiselect__tags',
          ],
          collaborator: [
            '[data-focus-field="collaborator"] .multiselect__input',
            '[data-focus-field="collaborator"] .multiselect__tags',
          ],
          concept: ['textarea[data-focus-field="concept"]'],
          amount: ['#fee-modal-amount'],
          expensesCount: ['input[data-focus-field="expensesCount"]'],
          firstInstallmentOption: [
            '[data-focus-field="firstInstallmentOption"] .multiselect__input',
            '[data-focus-field="firstInstallmentOption"] .multiselect__tags',
          ],
          periodicityOption: [
            '[data-focus-field="periodicityOption"] .multiselect__input',
            '[data-focus-field="periodicityOption"] .multiselect__tags',
          ],
        },
      });
    },
  },
};
</script>


